import { useCallback, useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector, useStore } from "react-redux";
import { AppState } from "@Store/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { isPaginatedUrl } from "../SearchList/useSearchList";
import {
  getRecommendationEventContext,
  planRecommendations,
} from "../../data/recommendationsThunks";
import { hasStandardFilter, tokensFromFilters } from "../../utils/filterApplicationOrder";
import {
  getExpandAvailableCount,
  isGeoRadiusExpandFlow,
  resolveRadiusExpandEnabled,
} from "../../utils/geoRadiusExpand";
import { RECOMMENDATIONS_PAGE_SIZE, removedFilterFields } from "../../utils/relaxation";
import { tokenKey } from "../../utils/filterTokens";
import {
  buildAppliedFiltersLine,
  sectionViewedParams,
  selectRecommendationCopy,
} from "../../utils/recommendationCopy";
import { initialRecommendations } from "../../data/searchPageReducer";
import { RecommendationCardContext } from "../SearchListItem/types";
import { isAvailable } from "../SearchListItem/helper";
import { SEARCH_RECOMMENDATIONS_SOURCE } from "@Utils/searchRecommendationAttribution";

type Params = { isDesktop: boolean };

/**
 * Eligibility, trigger and infinite-scroll paging for the recommendation section (P1D-5464).
 * Planning starts the moment the main list reports no next page (HLD D5).
 */
const useRecommendations = ({ isDesktop }: Params) => {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const platform = isDesktop ? "desktop" : "mobile";

  const {
    listReadyStatus,
    next,
    meta,
    filters,
    region,
    isAgentViewOpened,
    isRadiusExpanded,
    radiusExpandEnabled,
    recommendations,
    mainItems,
    filterApplicationOrder,
  } = useSelector(
    ({ searchPageData }: AppState) => ({
      listReadyStatus: searchPageData.searchList.readyStatus,
      next: searchPageData.searchList.next,
      meta: searchPageData.searchList.meta,
      filters: searchPageData.filters,
      region: searchPageData.region,
      isAgentViewOpened: searchPageData.isAgentViewOpened,
      isRadiusExpanded: searchPageData.isRadiusExpanded,
      radiusExpandEnabled: searchPageData.radiusExpandEnabled,
      recommendations: searchPageData.recommendations,
      mainItems: searchPageData.searchList.items,
      filterApplicationOrder: searchPageData.filterApplicationOrder,
    }),
    shallowEqual,
  );
  // `recommendations` is absent when hydrating HTML cached before this feature shipped.
  const { status, items, pageLoading, stepOfItem, plan, summary } =
    recommendations ?? initialRecommendations;
  // Heading / subheading choice (P1D-5538); null until a plan exists.
  const copy = useMemo(() => (summary ? selectRecommendationCopy(summary) : null), [summary]);
  const appliedFiltersLine = useMemo(
    () =>
      summary
        ? buildAppliedFiltersLine(summary, tokensFromFilters(filters), filterApplicationOrder)
        : null,
    [summary, filters, filterApplicationOrder],
  );
  const mainItemsLength = mainItems?.length || 0;

  // FR-4: with a geo filter, the "view nearby" flow runs first. Wait while it can still expand.
  const geoFlow = isGeoRadiusExpandFlow(
    filters,
    region,
    resolveRadiusExpandEnabled(radiusExpandEnabled),
  );
  const nearbyPending = geoFlow && !isRadiusExpanded && getExpandAvailableCount(meta) > 0;

  const exhausted = listReadyStatus === "success" && (next == null || next === 0);
  const eligible =
    exhausted &&
    !nearbyPending &&
    !isAgentViewOpened &&
    !isPaginatedUrl() &&
    hasStandardFilter(filters);

  useEffect(() => {
    if (eligible && status === "idle") dispatch(planRecommendations());
  }, [eligible, status, dispatch]);

  const visible = (status === "ready" || status === "exhausted") && items.length > 0;

  // List C total for breaker placement (P1D-5517 FR-6). Keep it once the last page has loaded
  // ("exhausted"): dropping to 0 there removed the section's breaker after cards 11–15 (P1D-5525).
  const listCTotal = useMemo(() => {
    if ((status !== "ready" && status !== "exhausted") || !plan.length) return 0;
    return Math.min(
      15,
      plan.reduce((sum, step) => sum + (step.pool || 0), 0),
    );
  }, [status, plan]);

  // Infinite scroll (HLD D14) is `useSearchList`'s scroll handler: the section renders inside
  // the list it measures, and `loadMoreRecommendationsIfNeed` pages it once the list is exhausted.

  // FR-43 (v5): one event per appended page after the first.
  const reportedLength = useRef(0);
  useEffect(() => {
    if (reportedLength.current && items.length > reportedLength.current) {
      gtagEventEmitter({
        action: "search_recommendations_page_loaded",
        ...getRecommendationEventContext(store.getState(), platform),
        loaded_count_before: reportedLength.current,
        page_index: Math.ceil(items.length / RECOMMENDATIONS_PAGE_SIZE),
      });
    }
    reportedLength.current = items.length;
  }, [items.length, platform, store]);

  const onSectionSeen = useCallback(() => {
    gtagEventEmitter({
      action: "search_recommendations_section_viewed",
      ...getRecommendationEventContext(store.getState(), platform),
      // Includes `month_widened` (P1D-5529) for the stop step C_k — its only emission.
      ...sectionViewedParams(plan, summary ?? null, tokensFromFilters(filters)),
    });
  }, [plan, summary, platform, filters, store]);

  // One context object per card, kept across appended pages: a fresh object on every render
  // re-rendered each memoised card. A card's step and position never change once it is listed;
  // a new plan, filter set or platform starts a new cache.
  const cardContexts = useMemo(
    () => new Map<string, RecommendationCardContext>(),
    [plan, platform, filters],
  );
  const cardContextFor = useCallback(
    (item: any, position: number): RecommendationCardContext => {
      const cached = cardContexts.get(String(item?.id));
      if (cached) return cached;
      const stepIndex = stepOfItem[String(item?.id)] ?? plan[plan.length - 1]?.index ?? 1;
      const step = plan.find((s) => s.index === stepIndex) || plan[plan.length - 1];
      // Group ids removed cumulatively in the card's step (contract §7.3).
      const removed = step?.removedGroups || [];
      const applied = tokensFromFilters(filters);
      const context = {
        ...getRecommendationEventContext(store.getState(), platform),
        source: SEARCH_RECOMMENDATIONS_SOURCE,
        inventory_id: item?.id,
        is_sold_out: !isAvailable(item),
        position,
        step_index: stepIndex,
        // removed_filters: option tokens (pre-P1D-5538 shape); removed_filter_groups: group ids.
        ...removedFilterFields(applied, removed),
        remaining_filters: applied.filter((t) => !removed.includes(tokenKey(t))),
        month_widened: Boolean(step?.monthWidened),
      };
      cardContexts.set(String(item?.id), context);
      return context;
    },
    [cardContexts, stepOfItem, plan, platform, filters, store],
  );

  return {
    visible,
    items,
    pageLoading,
    mainItemsLength,
    onSectionSeen,
    cardContextFor,
    listCTotal,
    copy,
    appliedFiltersLine,
  };
};

export default useRecommendations;
